import { z } from "zod";
import { Id } from "../common/ids.js";
import { Locale, LocalizedText } from "../common/localized-text.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { OidcProvider, Tokens } from "./auth.routes.js";
export const Membership = z
    .object({
    membershipId: Id,
    enterpriseId: Id,
    enterpriseName: LocalizedText,
    roles: z.array(z.string()),
    status: z.enum(["invited", "active", "left"]),
})
    .meta({ id: "Membership" });
export const Me = z
    .object({
    id: Id,
    displayName: z.string(),
    avatarFileId: Id.nullable(),
    email: z.email().nullable(),
    phone: z.string().nullable(),
    showPhoneToColleagues: z
        .boolean()
        .describe("Colleagues see the phone number in the directory only when true"),
    locale: Locale,
    timeZone: z.string(),
    mfaEnabled: z.boolean(),
    /** True when a role in the active enterprise requires MFA and the user has not enrolled (ADR 0015). */
    mfaEnrolmentRequired: z.boolean(),
    signInMethods: z.array(z.union([z.enum(["email", "phone"]), OidcProvider])),
    activeEnterpriseId: Id.nullable(),
    memberships: z.array(Membership),
})
    .meta({ id: "Me" });
export const getMe = defineRoute({
    id: "identity.me.get",
    method: "GET",
    path: "/v1/me",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "The signed-in user with sign-in methods, MFA state, and memberships",
    prd: ["IAM-2"],
    responses: { 200: { description: "The current user", schema: Me } },
    errors: ["AUTH_REQUIRED"],
});
export const updateMe = defineRoute({
    id: "identity.me.update",
    method: "PATCH",
    path: "/v1/me",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Update name, avatar, locale, time zone, or whether colleagues see the phone number",
    request: {
        body: z.object({
            displayName: z.string().min(1).max(100).optional(),
            avatarFileId: Id.nullable().optional(),
            locale: Locale.optional(),
            timeZone: z.string().min(1).max(64).optional(),
            showPhoneToColleagues: z.boolean().optional(),
        }),
    },
    responses: { 200: { description: "The updated user", schema: Me } },
    errors: ["AUTH_REQUIRED", "VALIDATION_FAILED"],
});
export const DeviceView = z
    .object({
    id: Id,
    client: z.enum(["web", "desktop"]),
    name: z.string(),
    current: z.boolean(),
    revoked: z.boolean(),
    lastSeenAt: Timestamp.nullable(),
    createdAt: Timestamp,
})
    .meta({ id: "DeviceView" });
export const listDevices = defineRoute({
    id: "identity.devices.list",
    method: "GET",
    path: "/v1/me/devices",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "The signed-in user's devices, the current one flagged",
    prd: ["IAM-3"],
    responses: { 200: { description: "Devices", schema: z.object({ items: z.array(DeviceView) }) } },
    errors: ["AUTH_REQUIRED"],
});
export const revokeDevice = defineRoute({
    id: "identity.devices.revoke",
    method: "DELETE",
    path: "/v1/me/devices/{deviceId}",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Revoke a device and every session on it",
    prd: ["IAM-3"],
    request: { params: z.object({ deviceId: Id }) },
    responses: { 204: { description: "Revoked" } },
    errors: ["AUTH_REQUIRED", "NOT_FOUND"],
});
const OneTimeCode = z.string().regex(/^\d{6}$/);
export const TotpEnrolment = z
    .object({
    otpauthUri: z.string().startsWith("otpauth://totp/"),
    secret: z.string().describe("Base32 secret for manual entry in an authenticator app"),
    expiresIn: z.number().int().positive().describe("Seconds left to confirm"),
})
    .meta({ id: "TotpEnrolment" });
export const enrolTotp = defineRoute({
    id: "identity.mfa.totp.enrol",
    method: "POST",
    path: "/v1/me/mfa/totp",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Start TOTP enrolment; the secret is kept pending for 10 minutes until confirmed",
    prd: ["IAM-1"],
    responses: { 200: { description: "Pending enrolment", schema: TotpEnrolment } },
    errors: ["AUTH_REQUIRED", "INVALID_TRANSITION"],
});
export const confirmTotp = defineRoute({
    id: "identity.mfa.totp.confirm",
    method: "POST",
    path: "/v1/me/mfa/totp/confirm",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Confirm enrolment with a code from the app; returns tokens with full permissions and a fresh step-up",
    prd: ["IAM-1"],
    request: { body: z.object({ code: OneTimeCode }) },
    responses: { 200: { description: "TOTP enabled", schema: Tokens } },
    errors: ["AUTH_REQUIRED", "INVALID_CODE", "INVALID_TRANSITION", "RATE_LIMITED"],
});
export const removeTotp = defineRoute({
    id: "identity.mfa.totp.remove",
    method: "DELETE",
    path: "/v1/me/mfa/totp",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: ["step-up"],
    summary: "Turn TOTP off; refused while a role in any enterprise requires it",
    prd: ["IAM-1"],
    responses: { 204: { description: "TOTP removed" } },
    errors: ["AUTH_REQUIRED", "STEP_UP_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const linkIdentity = defineRoute({
    id: "identity.identities.link",
    method: "POST",
    path: "/v1/me/identities/{provider}",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Link Google or Microsoft to the signed-in user by completing an authorization started with authorize",
    prd: ["IAM-1"],
    request: {
        params: z.object({ provider: OidcProvider }),
        body: z.object({
            code: z.string().min(1),
            codeVerifier: z.string().min(43).max(128),
            redirectUri: z.url(),
            state: z.string().min(16).max(256),
        }),
    },
    responses: { 204: { description: "Linked" } },
    errors: ["AUTH_REQUIRED", "INVALID_CODE", "DUPLICATE_REGISTRATION", "NOT_FOUND"],
});
export const unlinkIdentity = defineRoute({
    id: "identity.identities.unlink",
    method: "DELETE",
    path: "/v1/me/identities/{provider}",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: ["step-up-any"],
    summary: "Unlink a provider; refused when it is the last way to sign in",
    prd: ["IAM-1"],
    request: { params: z.object({ provider: OidcProvider }) },
    responses: { 204: { description: "Unlinked" } },
    errors: ["AUTH_REQUIRED", "STEP_UP_REQUIRED", "NOT_FOUND", "INVALID_TRANSITION"],
});
/** A person's export of their personal data (PDPA, GDPR; IM-8, card 083). */
export const DataExport = z
    .object({
    id: Id,
    status: z.enum(["queued", "building", "ready", "failed", "expired"]),
    requestedAt: Timestamp,
    readyAt: Timestamp.nullable(),
    expiresAt: Timestamp.nullable().describe("When the archive is deleted, 7 days after it is ready"),
    file: z
        .object({
        id: Id.describe("Downloaded through the files API, by this person alone"),
        sizeBytes: z.number().int().nonnegative(),
        sha256: z.string().regex(/^[0-9a-f]{64}$/),
    })
        .nullable()
        .describe("Set when ready: a ZIP of export.json, the person's files, and manifest.json with each entry's SHA-256"),
})
    .meta({ id: "DataExport" });
export const requestDataExport = defineRoute({
    id: "identity.data-exports.request",
    method: "POST",
    path: "/v1/me/data-exports",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: ["step-up-any"],
    summary: "Ask for an archive of one's personal data, built in the background; one at a time, and one a day (429 RATE_LIMITED otherwise)",
    prd: ["IM-8"],
    responses: { 202: { description: "The export, queued", schema: DataExport } },
    errors: ["AUTH_REQUIRED", "STEP_UP_REQUIRED", "RATE_LIMITED"],
});
export const getDataExport = defineRoute({
    id: "identity.data-exports.get",
    method: "GET",
    path: "/v1/me/data-exports/{exportId}",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "One of one's own exports; the latest is `latest`",
    prd: ["IM-8"],
    request: { params: z.object({ exportId: z.union([Id, z.literal("latest")]) }) },
    responses: { 200: { description: "The export", schema: DataExport } },
    errors: ["AUTH_REQUIRED", "NOT_FOUND"],
});
/** A person's request that their personal data be deleted (PDPA, GDPR; card 084). */
export const DeletionRequest = z
    .object({
    id: Id,
    status: z.enum(["pending", "cancelled", "done"]),
    requestedAt: Timestamp,
    effectiveAt: Timestamp.describe("7 days after the request; until then the person may cancel it"),
    cancelledAt: Timestamp.nullable(),
})
    .meta({ id: "DeletionRequest" });
export const requestDeletion = defineRoute({
    id: "identity.deletion-requests.request",
    method: "POST",
    path: "/v1/me/deletion-requests",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: ["step-up-any"],
    summary: "Ask that one's personal data be deleted, typing one's email (or phone) again; it takes effect in 7 days. Refused with 409 LAST_ADMIN, naming the enterprises in `errors`, while the person is an open enterprise's only admin",
    prd: ["IM-8"],
    request: { body: z.object({ confirm: z.string().trim().min(1).max(254) }) },
    responses: { 201: { description: "The request", schema: DeletionRequest } },
    errors: ["AUTH_REQUIRED", "STEP_UP_REQUIRED", "VALIDATION_FAILED", "INVALID_TRANSITION", "LAST_ADMIN"],
});
export const getDeletionRequest = defineRoute({
    id: "identity.deletion-requests.current",
    method: "GET",
    path: "/v1/me/deletion-requests/current",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "The person's pending deletion request, if any",
    prd: ["IM-8"],
    responses: { 200: { description: "The pending request", schema: DeletionRequest } },
    errors: ["AUTH_REQUIRED", "NOT_FOUND"],
});
export const cancelDeletion = defineRoute({
    id: "identity.deletion-requests.cancel",
    method: "POST",
    path: "/v1/me/deletion-requests/{requestId}/cancel",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Cancel one's pending deletion request before it takes effect",
    prd: ["IM-8"],
    request: { params: z.object({ requestId: Id }) },
    responses: { 200: { description: "The cancelled request", schema: DeletionRequest } },
    errors: ["AUTH_REQUIRED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const meRoutes = [
    getMe,
    updateMe,
    listDevices,
    revokeDevice,
    enrolTotp,
    confirmTotp,
    removeTotp,
    linkIdentity,
    unlinkIdentity,
    requestDataExport,
    getDataExport,
    requestDeletion,
    getDeletionRequest,
    cancelDeletion,
];
//# sourceMappingURL=me.routes.js.map